import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../_css/Cost.module.css';
import { Card, KpiCard, CardTitle, TableHeader, TableCell, Badge, Bar } from '../UI';
import { Coins, PieChart, Activity, Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { getPricingCreditUsage, updateOwnContractBillingEmail, toggleOwnContractRenewal, getOwnContractHistory, updateOwnPoNumbers, getOwnPoStatus, restartOwnContract, startOwnNewContract } from '../../requests';
import { notifyError, notifySuccess } from '../../utils/helper';
import { validateEmail } from '../helper/validators';
import { formatTermDay } from '../helper/formatters';
import useRole from '../../hooks/getRole';
import { CYCLE_SELECT_OPTIONS, formatCycleLabel, TIER_SELECT_OPTIONS } from '../Pricing/pricingTransforms';
import { Btn } from '../Pricing/shared';
import GeneratePersonaConfirmModal from '../Modals/GeneratePersonaConfirmModal';

const emptyState = {
  summary: null,
  selectedMonth: null,
  availableMonths: [],
  byUser: [],
  activity: [],
  byAction: [],
  monthlyTrend: [],
};

const NA = 'Not available';
const STAT_LOADING = '-';
const MONTHS_LIMIT = 5;
const ACTIVITY_PAGE = 1;
const ACTIVITY_LIMIT = 10;

// Ledger rows whose "credits" value is a snapshot of the plan's included allotment,
// not an actual usage delta - these are the ones that show the raw sentinel number for
// an Unlimited contract and need to read "Unlimited" instead.
// "Plan credits forfeited" (INV-09) is the old plan's remaining allotment, so it counts too.
const CREDIT_ALLOTMENT_ACTIONS = new Set(['Contract', 'Plan credits forfeited', 'max credits change']);

// Plain <input> elements on this page have no shared className to style them, so
// give them an explicit look (matches MonthFilterBar's .monthFilterSelect) rather
// than relying on the browser's bare default border, which barely shows up.
const TEXT_INPUT_STYLE = {
  padding: '8px 12px',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: 8,
  background: '#fff',
  color: '#363636',
  fontSize: 13,
  fontFamily: 'inherit',
};

const emptyPagination = {
  total: 0,
  totalPages: 1,
  currentPage: 1,
  perPage: ACTIVITY_LIMIT,
  hasNextPage: false,
  hasPrevPage: false,
};

function LoadingInline() {
  return (
    <div className={styles.loadingBlock}>
      <span className={styles.loadingSpinner} aria-hidden />
      <span>Loading...</span>
    </div>
  );
}

function formatCredits(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return NA;
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatEuro(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return NA;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(n);
}

function formatCreditDelta(type, credits) {
  const n = Number(credits);
  if (!Number.isFinite(n)) return NA;
  const abs = formatCredits(Math.abs(n));
  if (type === 'refund') return `+${abs}`;
  if (type === 'deduct') return `-${abs}`;
  return abs;
}

function formatActivityDate(iso) {
  if (!iso) return NA;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return NA;
  }
}

// INV-23 / #30: contract dates (UTC days) as DD.MM.YYYY, like the invoice mail.
// end_date is the renewal day; the period's last usable day is the day
// before. Use formatLastUsableDay wherever a period *ends*, formatContractDay
// for a start or where it *renews*.
function formatContractDay(iso) {
  return iso ? formatTermDay(iso) : NA;
}

function formatLastUsableDay(iso) {
  return iso ? formatTermDay(iso, { lastUsableDay: true }) : NA;
}

// Local timestamps (PO blocked since, today), not contract days.
function formatTermDate(iso) {
  if (!iso) return NA;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return NA;
  }
}

function displayText(loading, value, fallback = NA) {
  if (loading) return STAT_LOADING;
  if (value == null || value === '') return fallback;
  return value;
}

function displayCredits(loading, value) {
  if (loading) return STAT_LOADING;
  return formatCredits(value);
}

function MonthFilterBar({ loading, availableMonths, value, onChange, label }) {
  return (
    <div className={styles.monthFilterBar}>
      <label className={styles.monthFilterLabel} htmlFor="credit-month-filter">
        Month
      </label>
      <select
        id="credit-month-filter"
        className={styles.monthFilterSelect}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading || availableMonths.length === 0}
      >
        {availableMonths.length === 0 ? (
          <option value="">{loading ? 'Loading…' : 'No data'}</option>
        ) : (
          availableMonths.map((month) => (
            <option key={month.yearMonth} value={month.yearMonth}>
              {month.label}
            </option>
          ))
        )}
      </select>
      {label && <span className={styles.monthFilterHint}>Showing {label}</span>}
    </div>
  );
}

export default function AdminCreditUsageView() {
  const role = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(emptyState);
  const [loading, setLoading] = useState(true);
  const [selectedYearMonth, setSelectedYearMonth] = useState('');
  const [activityPage, setActivityPage] = useState(ACTIVITY_PAGE);
  const [activityPagination, setActivityPagination] = useState(emptyPagination);
  const [billingEmailInput, setBillingEmailInput] = useState('');
  const [savingBillingEmail, setSavingBillingEmail] = useState(false);
  const [poNumberInput, setPoNumberInput] = useState('');
  const [poNumberNextInput, setPoNumberNextInput] = useState('');
  const [savingPoNumbers, setSavingPoNumbers] = useState(false);
  const [confirmingRenewalToggle, setConfirmingRenewalToggle] = useState(false);
  const [togglingRenewal, setTogglingRenewal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  // undefined = not loaded yet, null = could not be loaded (fall back to the
  // contract summary), object = { po_number, po_number_next, po_blocked, ... }
  const [poStatus, setPoStatus] = useState(undefined);
  const [confirmingRestart, setConfirmingRestart] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [newTier, setNewTier] = useState('S');
  const [newCycle, setNewCycle] = useState('monthly');
  const [confirmingNewContract, setConfirmingNewContract] = useState(false);
  const [startingNewContract, setStartingNewContract] = useState(false);

  const poStatusLoaded = poStatus !== undefined;
  const poBlocked = Boolean(poStatus?.po_blocked);
  // The cancelled contract has ended: no contract runs, a new one can be started.
  const cancelledEnded = Boolean(poStatus?.cancelled_ended);

  useEffect(() => {
    if (String(role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [role, navigate]);

  // F-05: the PO numbers and the "paused for a missing PO" state come from
  // their own endpoint, which also works while the agency has no active contract.
  const loadPoStatus = useCallback(async () => {
    try {
      const response = await getOwnPoStatus();
      const payload = response?.data || {};
      setPoStatus({
        po_number: payload.po_number ?? '',
        po_number_next: payload.po_number_next ?? '',
        po_blocked: Boolean(payload.po_blocked),
        blocked_since: payload.blocked_since ?? null,
        last_cycle: payload.last_cycle ?? null,
        billing_email: payload.billing_email ?? '',
        cancelled_ended: Boolean(payload.cancelled_ended),
        ended_on: payload.ended_on ?? null,
        new_contract_options: Array.isArray(payload.new_contract_options) ? payload.new_contract_options : [],
      });
      if (payload.cancelled_ended) {
        // Default to the old contract's tier and cycle.
        if (['S', 'M', 'L'].includes(payload.last_tier)) setNewTier(payload.last_tier);
        if (['monthly', 'yearly'].includes(String(payload.last_cycle || '').toLowerCase())) {
          setNewCycle(String(payload.last_cycle).toLowerCase());
        }
      }
    } catch (error) {
      setPoStatus(null);
      notifyError(error?.response?.data?.message || 'Failed to load PO numbers');
    }
  }, []);

  useEffect(() => {
    if (String(role || '').toLowerCase() !== 'admin') return;
    loadPoStatus();
  }, [role, loadPoStatus]);

  useEffect(() => {
    setActivityPage(ACTIVITY_PAGE);
  }, [selectedYearMonth]);

  useEffect(() => {
    if (String(role || '').toLowerCase() !== 'admin') return undefined;
    if (!poStatusLoaded) return undefined;
    // Paused for a missing PO: there is no active contract, so the usage call would
    // only answer "Contract not found". The banner explains the state instead.
    // Same after a cancelled contract ended (the "new contract" card).
    if (poBlocked || cancelledEnded) {
      setData(emptyState);
      setActivityPagination(emptyPagination);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = {
          page: activityPage,
          limit: ACTIVITY_LIMIT,
          months: MONTHS_LIMIT,
        };
        if (selectedYearMonth) {
          const [year, month] = selectedYearMonth.split('-');
          params.year = year;
          params.month = month;
        }

        const response = await getPricingCreditUsage(params);
        if (cancelled) return;

        const payload = response?.data;
        const activityPayload = payload?.activity;
        const activityRows = Array.isArray(activityPayload?.data) ? activityPayload.data : [];
        const paginationData = activityPayload?.pagination || null;

        const nextData =
          payload && typeof payload === 'object'
            ? {
                summary: payload.summary ?? null,
                selectedMonth: payload.selectedMonth ?? null,
                availableMonths: Array.isArray(payload.availableMonths) ? payload.availableMonths : [],
                byUser: Array.isArray(payload.byUser) ? payload.byUser : [],
                activity: activityRows,
                byAction: Array.isArray(payload.byAction) ? payload.byAction : [],
                monthlyTrend: Array.isArray(payload.monthlyTrend) ? payload.monthlyTrend : [],
              }
            : emptyState;

        setData(nextData);
        setActivityPagination({
          total: paginationData?.total ?? 0,
          totalPages: paginationData?.totalPages ?? 1,
          currentPage: paginationData?.currentPage ?? activityPage,
          perPage: paginationData?.perPage ?? ACTIVITY_LIMIT,
          hasNextPage: paginationData?.hasNextPage ?? false,
          hasPrevPage: paginationData?.hasPrevPage ?? false,
        });

        if (!selectedYearMonth && nextData.selectedMonth?.yearMonth) {
          setSelectedYearMonth(nextData.selectedMonth.yearMonth);
        } else if (
          selectedYearMonth &&
          nextData.availableMonths.length > 0 &&
          !nextData.availableMonths.some((m) => m.yearMonth === selectedYearMonth)
        ) {
          setSelectedYearMonth(nextData.selectedMonth?.yearMonth || nextData.availableMonths[0].yearMonth);
        }
      } catch (error) {
        if (!cancelled) {
          setData(emptyState);
          setActivityPagination(emptyPagination);
          notifyError(error?.response?.data?.message || 'Failed to fetch credit usage');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role, selectedYearMonth, activityPage, poStatusLoaded, poBlocked, cancelledEnded]);

  // While paused there is no contract summary, so the stored address comes from
  // the PO status instead. Saving it writes to the paused contract, and the
  // restart carries it into the new period.
  useEffect(() => {
    setBillingEmailInput(data.summary?.billing_email ?? poStatus?.billing_email ?? '');
  }, [data.summary?.billing_email, poStatus?.billing_email]);

  // Stored PO numbers: from the PO status endpoint, the contract summary only as a
  // fallback when that endpoint could not be loaded.
  const poSource = poStatus || data.summary;
  const savedBillingEmail = data.summary?.billing_email ?? poStatus?.billing_email ?? '';
  const savedPoNumber = poSource?.po_number ?? '';
  const savedPoNumberNext = poSource?.po_number_next ?? '';

  useEffect(() => {
    setPoNumberInput(savedPoNumber);
    setPoNumberNextInput(savedPoNumberNext);
  }, [savedPoNumber, savedPoNumberNext]);

  // Sends only the fields that changed, so saving the current PO never blanks a
  // next-year PO (and vice versa). Saving never starts a period.
  const handleSavePoNumbers = async () => {
    const payload = {};
    if (poNumberInput.trim() !== savedPoNumber) payload.po_number = poNumberInput.trim();
    if (poNumberNextInput.trim() !== savedPoNumberNext) payload.po_number_next = poNumberNextInput.trim();
    if (Object.keys(payload).length === 0) return;

    try {
      setSavingPoNumbers(true);
      const response = await updateOwnPoNumbers(payload);
      const saved = {
        po_number: payload.po_number ?? savedPoNumber,
        po_number_next: payload.po_number_next ?? savedPoNumberNext,
      };
      setPoStatus((prev) =>
        prev
          ? { ...prev, ...saved, po_blocked: response?.data?.po_blocked ?? prev.po_blocked }
          : prev
      );
      setData((prev) => ({
        ...prev,
        summary: prev.summary ? { ...prev.summary, ...saved } : prev.summary,
      }));
      notifySuccess(response?.data?.message || 'PO number saved');
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update PO number');
    } finally {
      setSavingPoNumbers(false);
    }
  };

  const handleRestart = async () => {
    try {
      setRestarting(true);
      const response = await restartOwnContract();
      setConfirmingRestart(false);
      notifySuccess(response?.data?.message || 'New period started');
      await loadPoStatus();
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to start a new period');
    } finally {
      setRestarting(false);
    }
  };

  const handleStartNewContract = async () => {
    try {
      setStartingNewContract(true);
      const response = await startOwnNewContract({ tier: newTier, cycle: newCycle });
      setConfirmingNewContract(false);
      notifySuccess(response?.data?.message || 'New contract started');
      await loadPoStatus();
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to start a new contract');
    } finally {
      setStartingNewContract(false);
    }
  };

  const newContractOption = (poStatus?.new_contract_options || []).find(
    (o) => o.tier === newTier && o.cycle === newCycle
  );
  const newTierLabel = TIER_SELECT_OPTIONS.find((o) => o.value === newTier)?.label ?? newTier;

  const restartCycleLabel = formatCycleLabel(poStatus?.last_cycle || 'monthly').toLowerCase();
  const restartIsYearly = String(poStatus?.last_cycle || '').toLowerCase() === 'yearly';

  const handleSaveBillingEmail = async () => {
    const trimmed = billingEmailInput.trim();
    if (trimmed && !validateEmail(trimmed)) {
      notifyError('Please enter a valid billing email address');
      return;
    }

    try {
      setSavingBillingEmail(true);
      await updateOwnContractBillingEmail({ billing_email: trimmed });
      setData((prev) => ({
        ...prev,
        summary: prev.summary ? { ...prev.summary, billing_email: trimmed } : prev.summary,
      }));
      notifySuccess('Billing email updated');
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update billing email');
    } finally {
      setSavingBillingEmail(false);
    }
  };

  const summary = data.summary;
  const byUser = data.byUser;
  const activity = data.activity;
  const byAction = data.byAction;
  const monthlyTrend = data.monthlyTrend;
  const availableMonths = data.availableMonths;
  const selectedMonthLabel = data.selectedMonth?.label || NA;

  const maxActionCredits = useMemo(
    () => Math.max(0, ...byAction.map((row) => Number(row.creditsUsed) || 0)),
    [byAction],
  );

  const maxUserCredits = useMemo(
    () => Math.max(0, ...byUser.map((row) => Number(row.creditsUsed) || 0)),
    [byUser],
  );

  const isUnlimited = summary?.tier === 'U';
  const isTrial = summary?.tier === 'T';
  const canCancel = !loading && summary && !isTrial && !isUnlimited;
  const isCancelled = summary?.auto_renew === false;

  const handleToggleRenewal = async () => {
    try {
      setTogglingRenewal(true);
      const nextAutoRenew = isCancelled;
      await toggleOwnContractRenewal({ auto_renew: nextAutoRenew });
      setData((prev) => ({
        ...prev,
        summary: prev.summary ? { ...prev.summary, auto_renew: nextAutoRenew } : prev.summary,
      }));
      notifySuccess(nextAutoRenew ? 'Contract renewal resumed' : 'Contract cancelled');
      setConfirmingRenewalToggle(false);
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update contract renewal');
    } finally {
      setTogglingRenewal(false);
    }
  };

  const contractSub = loading
    ? STAT_LOADING
    : summary
      ? `${String(summary.tier || '—').toUpperCase()} · ${formatCycleLabel(summary.cycle)}`
      : NA;

  const renewalStatusText = () => {
    if (loading || !summary) return STAT_LOADING;
    if (isUnlimited) return 'No expiry';
    if (isTrial) return `Ends ${formatLastUsableDay(summary.end_date)} (trial, does not renew)`;
    if (isCancelled) return `Ends ${formatLastUsableDay(summary.end_date)} (cancelled, will not renew)`;
    return `Renews automatically on ${formatContractDay(summary.end_date)}`;
  };

  const handleToggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && !historyLoaded) {
      try {
        setHistoryLoading(true);
        const response = await getOwnContractHistory({ page: 1, limit: 20 });
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        setHistoryRows(rows);
        setHistoryLoaded(true);
      } catch (error) {
        notifyError(error?.response?.data?.message || 'Failed to load contract history');
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  if (String(role || '').toLowerCase() !== 'admin') {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1 className={styles.title}>Credit usage</h1>
        <p className={styles.subtitle}>
          Credits consumed against your agency contract. Amounts are shown in credits only.
        </p>
      </header>

      {poBlocked && (
        <Card>
          <CardTitle sub={`Since ${formatTermDate(poStatus?.blocked_since)}`}>Service paused: PO number missing</CardTitle>
          <div style={{ fontSize: 13, color: '#363636', marginBottom: 12 }}>
            The last billing period ended without a PO number on file, so no new period was started. You can still
            view your results, but new simulations are blocked. Save the current fiscal year&apos;s PO number below,
            then start a new period. It starts today; days without service are not charged.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Btn
              primary
              sm
              onClick={() => setConfirmingRestart(true)}
              disabled={restarting || !savedPoNumber.trim()}
            >
              {restarting ? 'Starting…' : 'Start a new period today'}
            </Btn>
            {!savedPoNumber.trim() && (
              <span style={{ fontSize: 12, color: '#8a8a8a' }}>
                Save the current fiscal year&apos;s PO number first.
              </span>
            )}
          </div>
        </Card>
      )}

      {cancelledEnded && (
        <Card>
          <CardTitle sub={`Ended ${formatLastUsableDay(poStatus?.ended_on)} (cancelled)`}>No running contract</CardTitle>
          <div style={{ fontSize: 13, color: '#363636', marginBottom: 12 }}>
            Your contract was cancelled and has ended. You can still view your results, but new simulations need a
            running contract. Choose a tier and cycle to start a new contract today at the current list price.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <select
              aria-label="Tier"
              style={TEXT_INPUT_STYLE}
              value={newTier}
              onChange={(e) => setNewTier(e.target.value)}
              disabled={startingNewContract}
            >
              {TIER_SELECT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Cycle"
              style={TEXT_INPUT_STYLE}
              value={newCycle}
              onChange={(e) => setNewCycle(e.target.value)}
              disabled={startingNewContract}
            >
              {CYCLE_SELECT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 13, color: '#363636' }}>
              {newContractOption
                ? `${formatEuro(newContractOption.price)} / month · ${formatCredits(newContractOption.credits)} credits`
                : 'No list price for this choice'}
            </span>
            <Btn
              primary
              sm
              onClick={() => setConfirmingNewContract(true)}
              disabled={startingNewContract || !newContractOption || !savedPoNumber.trim()}
            >
              {startingNewContract ? 'Starting…' : 'Start a new contract'}
            </Btn>
            {!savedPoNumber.trim() && (
              <span style={{ fontSize: 12, color: '#8a8a8a' }}>
                Save the current fiscal year&apos;s PO number first.
              </span>
            )}
          </div>
        </Card>
      )}

      <div className={styles.kpiGrid}>
        <KpiCard
          label="Credits used"
          value={displayCredits(loading, summary?.used_credits)}
          sub={contractSub}
          icon={<Activity size={24} />}
        />
        <KpiCard
          label="Included credits"
          value={isUnlimited ? 'Unlimited' : displayCredits(loading, summary?.max_credits)}
          sub="Current contract allowance"
          icon={<PieChart size={24} />}
        />
        <KpiCard
          label="Top-up credits"
          value={displayCredits(loading, summary?.topup_credits)}
          sub="Prepaid extra credits"
          icon={<Coins size={24} />}
        />
        <KpiCard
          label="Remaining balance"
          value={isUnlimited ? 'Unlimited' : displayCredits(loading, summary?.balance)}
          sub={
            loading
              ? STAT_LOADING
              : isUnlimited
                ? 'No credit cap'
                : summary
                  ? `${formatCredits(summary.plan_remaining)} plan + ${formatCredits(summary.topup_credits ?? 0)} top-up`
                  : NA
          }
          icon={<Wallet size={24} />}
        />
      </div>

      <Card>
        <CardTitle sub="Full contract details">Contract</CardTitle>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <tbody>
              <tr className={styles.tableRow}>
                <TableCell bold>Tier</TableCell>
                <TableCell>{displayText(loading, summary?.tier?.toUpperCase())}</TableCell>
                <TableCell bold>Billing cycle</TableCell>
                <TableCell>{loading ? STAT_LOADING : formatCycleLabel(summary?.cycle)}</TableCell>
              </tr>
              <tr className={styles.tableRow}>
                <TableCell bold>Term start</TableCell>
                <TableCell>{loading ? STAT_LOADING : formatContractDay(summary?.start_date)}</TableCell>
                <TableCell bold>Term end</TableCell>
                <TableCell>{loading ? STAT_LOADING : isUnlimited ? 'No expiry' : formatLastUsableDay(summary?.end_date)}</TableCell>
              </tr>
              <tr className={styles.tableRow}>
                <TableCell bold>Used / included</TableCell>
                <TableCell>
                  {loading
                    ? STAT_LOADING
                    : isUnlimited
                      ? `${formatCredits(summary?.used_credits)} / Unlimited`
                      : `${formatCredits(summary?.used_credits)} / ${formatCredits(summary?.max_credits)}`}
                </TableCell>
                <TableCell bold>Available now</TableCell>
                <TableCell bold>{isUnlimited ? 'Unlimited' : displayCredits(loading, summary?.balance)}</TableCell>
              </tr>
              <tr className={styles.tableRow}>
                <TableCell bold>Plan price</TableCell>
                <TableCell>{loading ? STAT_LOADING : (summary?.plan_price != null ? formatEuro(summary.plan_price) : NA)}</TableCell>
                <TableCell bold>Renewal</TableCell>
                <TableCell>
                  {renewalStatusText()}
                  {canCancel && (
                    <div style={{ marginTop: 6 }}>
                      <Btn
                        ghost={isCancelled}
                        primary={!isCancelled}
                        sm
                        onClick={() => setConfirmingRenewalToggle(true)}
                        disabled={togglingRenewal}
                      >
                        {isCancelled ? 'Undo cancellation' : 'Cancel contract'}
                      </Btn>
                    </div>
                  )}
                </TableCell>
              </tr>
              {!isUnlimited && (
                <tr className={styles.tableRow}>
                  <TableCell bold>Top-up credits ordered</TableCell>
                  <TableCell>{loading ? STAT_LOADING : `${formatCredits(summary?.overage_credits ?? 0)} cr`}</TableCell>
                  <TableCell bold>Top-up charge</TableCell>
                  <TableCell>{loading ? STAT_LOADING : formatEuro(summary?.overage_eur ?? 0)}</TableCell>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.tableWrap} style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#363636', marginBottom: 4 }}>PO numbers</div>
          <div style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 8 }}>
            Fiscal year runs 20 June – 19 June. The current fiscal year's number is what's printed on the invoice mail and the statement PDF; the next one takes over automatically at the fiscal year boundary.
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#8a8a8a', marginBottom: 4 }} htmlFor="po-number-current">
                Current fiscal year
              </label>
              <input
                id="po-number-current"
                type="text"
                value={poNumberInput}
                onChange={(e) => setPoNumberInput(e.target.value)}
                placeholder="PO-123456"
                disabled={loading || savingPoNumbers}
                style={{ ...TEXT_INPUT_STYLE, width: 220, maxWidth: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#8a8a8a', marginBottom: 4 }} htmlFor="po-number-next">
                Next fiscal year
              </label>
              <input
                id="po-number-next"
                type="text"
                value={poNumberNextInput}
                onChange={(e) => setPoNumberNextInput(e.target.value)}
                placeholder="PO-123456"
                disabled={loading || savingPoNumbers}
                style={{ ...TEXT_INPUT_STYLE, width: 220, maxWidth: '100%' }}
              />
            </div>
            <Btn
              primary
              sm
              onClick={handleSavePoNumbers}
              disabled={
                loading ||
                savingPoNumbers ||
                (poNumberInput.trim() === savedPoNumber &&
                  poNumberNextInput.trim() === savedPoNumberNext)
              }
            >
              {savingPoNumbers ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        </div>
        <div className={styles.tableWrap} style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#363636', marginBottom: 4 }}>Billing email</div>
          <div style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 8 }}>
            Receives a copy of the invoice for this contract. Leave empty for no copy.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="email"
              value={billingEmailInput}
              onChange={(e) => setBillingEmailInput(e.target.value)}
              placeholder="billing@example.com"
              disabled={loading || savingBillingEmail}
              style={{ ...TEXT_INPUT_STYLE, width: 320, maxWidth: '100%', flex: '0 1 auto' }}
            />
            <span style={{ flexShrink: 0 }}>
              <Btn
                primary
                sm
                onClick={handleSaveBillingEmail}
                disabled={loading || savingBillingEmail || billingEmailInput.trim() === savedBillingEmail}
              >
                {savingBillingEmail ? 'Saving…' : 'Save'}
              </Btn>
            </span>
          </div>
        </div>
      </Card>

      <Card>
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggleHistory}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleHistory();
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          aria-expanded={historyOpen}
        >
          <CardTitle sub="Past billing periods for this agency">Contract history</CardTitle>
          {historyOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        {historyOpen && (
          <div className={styles.tableWrap} style={{ marginTop: 12 }}>
            <table className={styles.table}>
              <thead>
                <TableHeader columns={['Period', 'Tier', 'Cycle', 'Used / included', 'Plan price', 'Total charged']} />
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr className={styles.tableRow}>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <LoadingInline />
                    </td>
                  </tr>
                ) : historyRows.length === 0 ? (
                  <tr className={styles.tableRow}>
                    <td colSpan={6} className={styles.tableFootCell} style={{ textAlign: 'center' }}>
                      No past contract periods
                    </td>
                  </tr>
                ) : (
                  historyRows.map((row) => (
                    <tr key={row.id} className={styles.tableRow}>
                      <TableCell>{formatContractDay(row.start_date)} → {formatLastUsableDay(row.end_date)}</TableCell>
                      <TableCell>{String(row.tier || '—').toUpperCase()}</TableCell>
                      <TableCell>{formatCycleLabel(row.cycle)}</TableCell>
                      <TableCell>
                        {row.tier === 'U'
                          ? `${formatCredits(row.used_credits)} / Unlimited`
                          : `${formatCredits(row.used_credits)} / ${formatCredits(row.max_credits)}`}
                      </TableCell>
                      <TableCell>{formatEuro(row.plan_price)}</TableCell>
                      <TableCell bold>{formatEuro(row.total_eur)}</TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <MonthFilterBar
        loading={loading}
        availableMonths={availableMonths}
        value={selectedYearMonth}
        onChange={setSelectedYearMonth}
        label={selectedMonthLabel}
      />

      <Card>
        <CardTitle sub={`Credits consumed per user · ${selectedMonthLabel}`}>User-wise usage</CardTitle>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <TableHeader columns={['User', 'Credits used', 'Share']} />
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.tableRow}>
                  <td colSpan={3} style={{ padding: 0 }}>
                    <LoadingInline />
                  </td>
                </tr>
              ) : byUser.length === 0 ? (
                <tr className={styles.tableRow}>
                  <td colSpan={3} className={styles.tableFootCell} style={{ textAlign: 'center' }}>
                    No usage for this month
                  </td>
                </tr>
              ) : (
                byUser.map((row) => (
                  <tr key={row.userId ?? row.userName} className={styles.tableRow}>
                    <TableCell bold>{displayText(false, row.userName)}</TableCell>
                    <TableCell>{displayCredits(false, row.creditsUsed)}</TableCell>
                    <TableCell>
                      <div className={styles.inlineBarCell}>
                        <Bar
                          pct={
                            maxUserCredits > 0
                              ? ((Number(row.creditsUsed) || 0) / maxUserCredits) * 100
                              : 0
                          }
                          color="#6366f1"
                          showLabel={false}
                          height={8}
                        />
                      </div>
                    </TableCell>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardTitle sub={`Credit movements · ${selectedMonthLabel}`}>Recent activity</CardTitle>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <TableHeader columns={['Date', 'User', 'Activity', 'Type', 'Credits']} />
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.tableRow}>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <LoadingInline />
                  </td>
                </tr>
              ) : activity.length === 0 ? (
                <tr className={styles.tableRow}>
                  <td colSpan={5} className={styles.tableFootCell} style={{ textAlign: 'center' }}>
                    No activity for this month
                  </td>
                </tr>
              ) : (
                activity.map((row, index) => (
                  <tr key={`${row.date}-${row.action}-${index}`} className={styles.tableRow}>
                    <TableCell>{formatActivityDate(row.date)}</TableCell>
                    <TableCell>{displayText(false, row.userName)}</TableCell>
                    <TableCell bold>{displayText(false, row.action)}</TableCell>
                    <TableCell>
                      {row.type ? (
                        <Badge color={row.type === 'refund' ? '#22c55e' : '#6366f1'}>
                          {row.type}
                        </Badge>
                      ) : (
                        NA
                      )}
                    </TableCell>
                    <TableCell bold>
                      {isUnlimited && CREDIT_ALLOTMENT_ACTIONS.has(row.action)
                        ? 'Unlimited'
                        : formatCreditDelta(row.type, row.credits)}
                    </TableCell>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {activityPagination.totalPages > 1 && (
          <div className={styles.paginationBar}>
            <span className={styles.paginationMeta}>
              Page {activityPagination.currentPage} of {activityPagination.totalPages} ·{' '}
              {activityPagination.total} entries
            </span>
            <div className={styles.paginationActions}>
              <Btn
                ghost
                sm
                disabled={!activityPagination.hasPrevPage || loading}
                onClick={() => setActivityPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Btn>
              <Btn
                ghost
                sm
                disabled={!activityPagination.hasNextPage || loading}
                onClick={() => setActivityPage((prev) => prev + 1)}
              >
                Next
              </Btn>
            </div>
          </div>
        )}
      </Card>

      <div className={styles.twoColGrid}>
        <Card>
          <CardTitle>Credits by activity</CardTitle>
          <div className={styles.brandList}>
            {loading ? (
              <LoadingInline />
            ) : byAction.length === 0 ? (
              <div style={{ color: 'var(--muted, #6b7280)', padding: '8px 0' }}>{NA}</div>
            ) : (
              byAction.map((row, index) => (
                <div key={`${row.action}-${index}`} className={styles.brandRow}>
                  <span className={styles.brandLabel}>{displayText(false, row.action)}</span>
                  <div className={styles.brandBarWrap}>
                    <Bar
                      pct={
                        maxActionCredits > 0
                          ? ((Number(row.creditsUsed) || 0) / maxActionCredits) * 100
                          : 0
                      }
                      color="#6366f1"
                      showLabel={false}
                      height={8}
                    />
                  </div>
                  <span className={styles.brandCost}>
                    {displayCredits(false, row.creditsUsed)} cr
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card>
          <CardTitle>Monthly usage</CardTitle>
          <div className={styles.trendList}>
            {loading ? (
              <LoadingInline />
            ) : monthlyTrend.length === 0 ? (
              <div style={{ color: 'var(--muted, #6b7280)', padding: '8px 0' }}>{NA}</div>
            ) : (
              monthlyTrend.map((row, index) => (
                <div
                  key={row.yearMonth || index}
                  className={index < monthlyTrend.length - 1 ? styles.trendRow : styles.trendRowLast}
                >
                  <div>
                    <div className={styles.trendMonth}>{displayText(false, row.label)}</div>
                    <div className={styles.trendRuns}>Credits consumed</div>
                  </div>
                  <span className={styles.trendCost}>{displayCredits(false, row.creditsUsed)} cr</span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      <GeneratePersonaConfirmModal
        open={confirmingRenewalToggle}
        onClose={() => setConfirmingRenewalToggle(false)}
        onConfirm={handleToggleRenewal}
        disabled={togglingRenewal}
        title={isCancelled ? 'Undo cancellation?' : 'Cancel contract?'}
        message={
          isCancelled
            ? 'Your contract will renew as normal at the end of the current term.'
            : `Your contract will run until ${formatLastUsableDay(summary?.end_date)} and then not renew. You can undo this any time before then. Mediaplus will be notified by email.`
        }
      />

      <GeneratePersonaConfirmModal
        open={confirmingRestart}
        onClose={() => setConfirmingRestart(false)}
        onConfirm={handleRestart}
        disabled={restarting}
        title="Start a new period today?"
        message={`A new ${restartCycleLabel} period starts today, ${formatTermDate(new Date())}${
          restartIsYearly ? ', and runs 12 months' : ''
        }. Days without service are not charged.`}
      />

      <GeneratePersonaConfirmModal
        open={confirmingNewContract}
        onClose={() => setConfirmingNewContract(false)}
        onConfirm={handleStartNewContract}
        disabled={startingNewContract}
        title="Start a new contract?"
        message={`A new ${formatCycleLabel(newCycle).toLowerCase()} ${newTierLabel} contract starts today, ${formatTermDate(new Date())}${
          newCycle === 'yearly' ? ', runs 12 months' : ''
        } and renews automatically.${
          newContractOption
            ? ` Price: ${formatEuro(newContractOption.price)} / month, ${formatCredits(newContractOption.credits)} credits.`
            : ''
        }`}
      />
    </div>
  );
}
